/**
 * Email contact dialog component
 */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { sendClientContactEmail, isAutoGeneratedEmail, validateEmailFormat } from '../../services/api/emailApi';

/**
 * Dialog for sending emails to client contacts
 * @param {Object} props - Component props
 * @param {string} props.contactName - Name of the contact
 * @param {string} props.contactEmail - Email of the contact
 * @param {string} props.clientName - Name of the client
 * @param {Function} props.onClose - Function to call when dialog is closed
 * @returns {JSX.Element} EmailContactDialog component
 */
/**
 * NOTE: This modal uses React Portal to render at document.body level.
 * This avoids stacking context bugs caused by CSS filter on parent containers (e.g., .app-container.history-mode).
 */
const EmailContactDialog = ({ contactName, contactEmail, clientName, onClose }) => {
  const [subject, setSubject] = useState(`Regarding your upcoming conference with ${clientName}`);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email format
    if (!validateEmailFormat(contactEmail)) {
      onClose({
        success: false,
        message: 'Invalid email address format'
      });
      return;
    }

    // Check if email appears to be auto-generated
    if (isAutoGeneratedEmail(contactEmail)) {
      const proceed = window.confirm(
        'This email address appears to be auto-generated or placeholder. Do you want to proceed anyway?'
      );
      if (!proceed) {
        return;
      }
    }
    
    // Show sending state
    setIsSending(true);
    
    try {
      // Send email via API
      const result = await sendClientContactEmail({
        contactEmail,
        contactName,
        clientName,
        subject,
        message,
        senderName: 'ABTSource User' // Could be made configurable
      });
      
      setIsSending(false);
      
      if (result.success) {
        onClose({
          success: true,
          message: `Email sent successfully to ${contactName} (${contactEmail})`
        });
      } else {
        onClose({
          success: false,
          message: result.message || 'Failed to send email'
        });
      }
    } catch (error) {
      setIsSending(false);
      console.error('Email sending error:', error);
      onClose({
        success: false,
        message: 'Failed to send email. Please check your email configuration.'
      });
    }
  };

  // Render modal using React Portal to avoid stacking context issues with parent filters
  return ReactDOM.createPortal(
    (
      <div className="dialog-overlay">
        <div className="dialog-container">
          <div className="dialog-header">
            <h2>Send Email to Contact</h2>
            <button 
              className="close-btn" 
              onClick={() => onClose({ success: false })}
            >
              ×
            </button>
          </div>
          
          <div className="dialog-content">
            <div className="contact-info">
              <div className="info-row">
                <span className="info-label">Contact:</span>
                <span className="info-value">{contactName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{contactEmail}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Client:</span>
                <span className="info-value">{clientName}</span>
              </div>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  id="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  className="form-control"
                  rows={8}
                  placeholder="Enter your message here..."
                />
              </div>
              
              <div className="dialog-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => onClose({ success: false })}
                  disabled={isSending}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="send-btn"
                  disabled={isSending}
                >
                  {isSending ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
        
        <style jsx>{`
          .dialog-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            animation: fadeIn 0.2s ease-out;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          .dialog-container {
            background-color: white;
            border-radius: 8px;
            width: 500px;
            max-width: 90%;
            max-height: 90vh;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            display: flex;
            flex-direction: column;
            animation: slideIn 0.3s ease-out;
            overflow: hidden;
          }
          
          @keyframes slideIn {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          .dialog-header {
            padding: 16px;
            border-bottom: 1px solid #eee;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          
          .dialog-header h2 {
            margin: 0;
            font-size: 1.25rem;
            color: #333;
          }
          
          .close-btn {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #666;
            transition: color 0.2s;
          }
          
          .close-btn:hover {
            color: #000;
          }
          
          .dialog-content {
            padding: 20px;
            overflow-y: auto;
            flex: 1;
          }
          
          .contact-info {
            background-color: #f5f8ff;
            padding: 16px;
            border-radius: 6px;
            margin-bottom: 20px;
            border: 1px solid #e0e8ff;
          }
          
          .info-row {
            display: flex;
            margin-bottom: 10px;
            align-items: baseline;
          }
          
          .info-row:last-child {
            margin-bottom: 0;
          }
          
          .info-label {
            font-weight: 500;
            width: 80px;
            min-width: 80px;
            color: #555;
            font-size: 14px;
          }
          
          .info-value {
            flex: 1;
            word-break: break-word;
            font-size: 14px;
          }
          
          .form-group {
            margin-bottom: 20px;
          }
          
          .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #333;
            font-size: 14px;
          }
          
          .form-control {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
            min-height: 40px;
          }
          
          textarea.form-control {
            resize: vertical;
            min-height: 100px;
          }
          
          .form-control:focus {
            outline: none;
            border-color: #0074D9;
            box-shadow: 0 0 0 2px rgba(0, 116, 217, 0.2);
          }
          
          .dialog-actions {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 24px;
            padding-top: 20px;
            border-top: 1px solid #eee;
          }
          
          .cancel-btn, .send-btn {
            padding: 10px 20px;
            border-radius: 4px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 14px;
            min-height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .cancel-btn {
            background-color: #f8f9fa;
            border: 1px solid #ddd;
            color: #666;
            min-width: 100px;
          }
          
          .cancel-btn:hover {
            background-color: #e9ecef;
            border-color: #adb5bd;
            color: #495057;
          }
          
          .send-btn {
            background-color: #0074D9;
            border: 1px solid #0074D9;
            color: white;
            font-weight: 600;
            min-width: 120px;
          }
          
          .send-btn:hover {
            background-color: #0063b1;
            border-color: #0063b1;
          }
          
          .send-btn:disabled {
            background-color: #a0c5e8;
            border-color: #a0c5e8;
            cursor: not-allowed;
            opacity: 0.7;
          }
          
          .cancel-btn:disabled, .send-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    ),
    document.body
  );
};

export default EmailContactDialog;
